"use client";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import CustomSelect from "../ui/CustomSelect";
import { City } from "../../lib/interface";
import { formateCityName } from "../../lib/formateCityName";
import { useDateRange } from "../date/DateRangeContext";
import { ensureFromToParams } from "@/lib/formatHour";
import { useCityFromUrl, generateCityUrl } from "@/lib/cityUtils";
import { useCityContext } from "@/lib/CityContext";
import { track, setUserProperties } from "@/lib/tracking";

type SelectionRange = { startDate: Date; endDate: Date; key: string };

const CitySelectbox = ({
  width = "280px",
  height = "42px",
  backgroundColor = "#F4F4F4",
  boxShadow = "none",
  textSize = "16px",
  columns,
  mobileColumns,
  desktopColumns,
}: {
  width?: string;
  height?: string;
  backgroundColor?: string;
  boxShadow?: string;
  textSize?: string;
  columns?: number;
  mobileColumns?: number;
  desktopColumns?: number;
}) => {
  const pathname = usePathname();
  const router = useRouter();
  const { range } = useDateRange();
  const isSearchPage = pathname.includes("search");
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  
  // Use centralized city context
  const { cities, isLoading } = useCityContext();
  
  // Get city from URL using centralized context
  const currentCity = useCityFromUrl();
  
  useEffect(() => {
    // Set selected city ID based on URL when cities are loaded
    if (cities.length > 0 && currentCity._id) {
      setSelectedCityId(currentCity._id);
    }
  }, [pathname, currentCity._id, cities.length]);

  const selectedCity = cities.find((city: City) => city._id === selectedCityId);

  const handleCitySelect = (cityId: string) => {
    const city = cities.find((c) => c._id === cityId);
    if (!city || city._id === selectedCity?._id) return;
    
    setSelectedCityId(city._id);

    track('city_selected', { city: city.name });
    setUserProperties({ preferred_city: city.name });
  
    const formattedCityName = formateCityName(city.name);
    const path = isSearchPage ? 'bike-rentals/search' : 'bike-rentals';
    const searchParams = new URLSearchParams(window.location.search);
    
    // Add city ID to URL parameters for other API calls
    searchParams.set('cityId', city._id);
    
    // Add date range parameters
    const paramsWithDates = ensureFromToParams(
      [{ ...range, key: 'selection' } as SelectionRange], 
      searchParams
    );
    
    const url = generateCityUrl(formattedCityName, path, paramsWithDates);
    router.push(url);
  };
  // Show loading state while cities are being fetched
  if (isLoading) {
    return (
      <div
        style={{
          width,
          height,
          backgroundColor,
          boxShadow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          fontSize: textSize || "16px",
        }}
        className="animate-pulse"
      >
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-gray-300 rounded-full animate-bounce"></div>
          <span className="text-gray-500">Loading cities...</span>
        </div>
      </div>
    );
  }

  // Show no cities available state
  if (cities.length === 0 && !isLoading) {
    return (
      <div
        style={{
          width,
          height,
          backgroundColor,
          boxShadow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          fontSize: textSize || "16px",
        }}
        className="text-gray-500"
      >
        No cities available
      </div>
    );
  }

  // Filter cities to only show those where disabled === false
  const enabledCities = cities.filter((city) => city.disabled === false);

  return (
    <CustomSelect
      value={selectedCityId}
      options={enabledCities.map((city) => ({
        value: city._id,
        label: city.name,
      }))}
      onChange={handleCitySelect}
      placeholder="Select City Name"
      width={width}
      height={height}
      style={{ backgroundColor: backgroundColor, boxShadow: boxShadow,textWrap: "nowrap" }}
      textSize={textSize || "16px"}
      isCityDropdown={true}
      columns={columns}
      mobileColumns={mobileColumns}
      desktopColumns={desktopColumns}
    />
  );
};

export default CitySelectbox;
